import React, { useState, useEffect, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import bgAsset from '../../assets/background-asset.svg'
import bgAsset2 from './background1.svg'
import bgAsset3 from './background2.svg'
import bgAsset4 from './background3.svg'

import ThunderheadIntroCard from './Thunderhead'
import HyperswapIntroCard from './Hyperswap'
import LoopedhypeIntroCard from './Loopedhype'
import HyperbeatIntroCard from './HyperbeatIntroCard'

const bgs = [bgAsset, bgAsset2, bgAsset3, bgAsset4]

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const IntroCardStyled = styled.div<{ bg: string; opacity: number }>`
  animation: ${fadeUp} 0.5s ease-out;
  border-radius: 14px;
  background: #082822ab;
  padding: 32px;
  font-family: Unbounded;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-image: url(${(props) => props.bg});
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    opacity: ${(props) => props.opacity};
    transition: opacity 0.1s ease-in-out; /* Smooth transition for opacity changes */
  }

  h1 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 700;
    font-size: 20px;
    color: ${({ theme }) => theme.primary1};
  }
  p {
    font-weight: 400;
    font-size: 12px;
    margin-top: 0;
    margin-bottom: 0;
    color: #bec2da;
  }
  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 16px;
    h1 {
      font-size: 16px;
    }
    p {
      font-size: 10px;
    }
`};
`

interface IntroCardI {
  variant: 1 | 2 | 3 | 4 | 5
  children: string | JSX.Element | JSX.Element[] | Element | Element[] | any
  Key?: string
  desktopIconIsTransparent?: boolean
}

const IntroCard: React.FC<IntroCardI> = ({ variant, children, Key, desktopIconIsTransparent }) => {
  const seed = Key || 'default-seed'
  const [opacity, setOpacity] = useState(0.1)
  const [increasing, setIncreasing] = useState(true)
  const bg = useMemo(() => bgs[Math.floor(Math.random() * bgs.length)], [])

  useEffect(() => {
    const opacityInterval = setInterval(() => {
      setOpacity((prevOpacity) => {
        if (prevOpacity >= 0.25 && increasing) {
          setIncreasing(false) // Switch to decreasing
          return 0.25
        }
        if (prevOpacity <= 0.1 && !increasing) {
          setIncreasing(true) // Switch to increasing
          return 0.1
        }
        return increasing ? prevOpacity + 0.01 : prevOpacity - 0.01 // Adjust opacity smoothly
      })
    }, 100)

    return () => clearInterval(opacityInterval)
  }, [increasing])

  return (
    <>
      {variant === 1 && (
        <IntroCardStyled bg={bg} opacity={opacity} key={seed}>
          {children}
        </IntroCardStyled>
      )}
      {variant === 2 && <ThunderheadIntroCard key={seed}>{children}</ThunderheadIntroCard>}
      {variant === 3 && (
        <HyperswapIntroCard desktopIconIsTransparent={desktopIconIsTransparent} key={seed}>
          {children}
        </HyperswapIntroCard>
      )}
      {variant === 4 && <LoopedhypeIntroCard key={seed}>{children}</LoopedhypeIntroCard>}
      {variant === 5 && <HyperbeatIntroCard key={seed}>{children}</HyperbeatIntroCard>}
    </>
  )
}

export default IntroCard
